import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge } from '@/components/common/Badge';
import { Button } from '@/components/common/Button';
import { Card } from '@/components/common/Card';
import { Modal } from '@/components/common/Modal';
import { ErrorState, LoadingState } from '@/components/common/States';
import { SelectField, TextField } from '@/components/forms/Field';
import { PageHeader } from '@/components/layout/PageHeader';
import { useToast } from '@/components/feedback/ToastProvider';
import { useApiErrorHandler } from '@/hooks/useApiErrorHandler';
import { academicSubjectsApi, sectionsApi } from '@/services/api/endpoints/academic';
import { teachersApi } from '@/services/api/endpoints/staff';
import { teacherAssignmentsApi } from '@/services/api/endpoints/teaching';
import { queryKeys } from '@/services/api/queryKeys';
import { useAuth } from '@/features/auth/useAuth';
import { PERMISSIONS } from '@/types/permissions';
import type { AssignmentType, ClassTeacherRole, TeacherAssignment } from '@/types/domain';
import { formatDate, humanise } from '@/utils/format';

const TYPES: AssignmentType[] = ['PRIMARY_TEACHER', 'ASSISTANT_TEACHER', 'SUBSTITUTE_TEACHER'];

/**
 * Who teaches what, and who looks after each class.
 *
 * This is the screen the timetable and assignment screens depend on: a lesson
 * can only be scheduled against a teaching assignment, and work can only be set
 * under one. Nothing else grants the right to stand in front of a class.
 *
 * Assignments end by being dated out rather than deleted, because the timetable
 * entries and coursework beneath them stay real.
 */
export function TeacherAssignmentsPage() {
  const { can } = useAuth();
  const toast = useToast();
  const handleApiError = useApiErrorHandler();
  const queryClient = useQueryClient();

  const [sectionId, setSectionId] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [classTeacherOpen, setClassTeacherOpen] = useState(false);
  const [ending, setEnding] = useState<TeacherAssignment | null>(null);
  const [endDate, setEndDate] = useState('');
  const [form, setForm] = useState({
    teacherId: '',
    academicSubjectId: '',
    assignmentType: 'PRIMARY_TEACHER' as AssignmentType,
    effectiveFrom: '',
  });
  const [classTeacherForm, setClassTeacherForm] = useState({
    teacherId: '',
    role: 'CLASS_TEACHER' as ClassTeacherRole,
    effectiveFrom: '',
  });

  const sections = useQuery({
    queryKey: queryKeys.sections.list({ size: 200 }),
    queryFn: () => sectionsApi.list({ page: 0, size: 200 }),
  });
  const selectedSection = sections.data?.data.find((section) => section.id === sectionId);

  const assignments = useQuery({
    queryKey: queryKeys.teacherAssignments.list({ sectionId }),
    queryFn: () => teacherAssignmentsApi.list({ sectionId }),
    enabled: sectionId !== '',
  });

  const classTeachers = useQuery({
    queryKey: queryKeys.teacherAssignments.classTeachers(sectionId),
    queryFn: () => teacherAssignmentsApi.classTeachers(sectionId),
    enabled: sectionId !== '',
  });

  /* The subject list comes from the section's own curriculum: a subject from a
     different grade offering is refused by the backend, so offering it here
     would only produce errors. */
  const curriculum = useQuery({
    queryKey: queryKeys.academicSubjects.forOffering(selectedSection?.gradeOfferingId ?? '', 'ACTIVE'),
    queryFn: () => academicSubjectsApi.list(selectedSection?.gradeOfferingId ?? '', 'ACTIVE'),
    enabled: selectedSection?.gradeOfferingId !== undefined,
  });

  const teachers = useQuery({
    queryKey: queryKeys.teachers.list({ size: 200 }),
    queryFn: () => teachersApi.list({ page: 0, size: 200 }),
    enabled: formOpen || classTeacherOpen,
  });

  const invalidate = () =>
    void queryClient.invalidateQueries({ queryKey: queryKeys.teacherAssignments.all });

  const assign = useMutation({
    mutationFn: () =>
      teacherAssignmentsApi.create({
        teacherId: form.teacherId,
        academicSubjectId: form.academicSubjectId,
        sectionId,
        assignmentType: form.assignmentType,
        effectiveFrom: form.effectiveFrom,
      }),
    onSuccess: () => {
      toast.success('Teacher assigned');
      setFormOpen(false);
      setForm({ ...form, teacherId: '', academicSubjectId: '' });
      invalidate();
    },
    onError: (error) => handleApiError(error),
  });

  const assignClassTeacher = useMutation({
    mutationFn: () =>
      teacherAssignmentsApi.assignClassTeacher({
        teacherId: classTeacherForm.teacherId,
        sectionId,
        role: classTeacherForm.role,
        effectiveFrom: classTeacherForm.effectiveFrom,
      }),
    onSuccess: () => {
      toast.success('Class teacher assigned');
      setClassTeacherOpen(false);
      invalidate();
    },
    onError: (error) => handleApiError(error),
  });

  const end = useMutation({
    mutationFn: () => teacherAssignmentsApi.end(ending?.id ?? '', { effectiveTo: endDate }),
    onSuccess: () => {
      toast.success('Assignment ended. The lessons taught under it are kept.');
      setEnding(null);
      setEndDate('');
      invalidate();
    },
    onError: (error) => handleApiError(error),
  });

  const activeClassTeacher = classTeachers.data?.find(
    (entry) => entry.status === 'ACTIVE' && entry.role === 'CLASS_TEACHER',
  );

  return (
    <div className="space-y-6">
      <PageHeader
        title="Teaching assignments"
        description="Who teaches what. A lesson can only be timetabled, and work only set, against an assignment."
        action={
          can(PERMISSIONS.TEACHER_ASSIGN) && sectionId !== '' ? (
            <div className="flex gap-2">
              <Button variant="secondary" onClick={() => setClassTeacherOpen(true)}>
                Set class teacher
              </Button>
              <Button onClick={() => setFormOpen(true)}>Assign teacher</Button>
            </div>
          ) : undefined
        }
      />

      <Card className="p-5">
        <div className="max-w-md">
          <SelectField
            label="Class"
            value={sectionId}
            onChange={(event) => setSectionId(event.target.value)}
          >
            <option value="">Select a class…</option>
            {sections.data?.data.map((section) => (
              <option key={section.id} value={section.id}>
                {section.gradeName !== undefined ? `${section.gradeName} · ` : ''}
                {section.name}
              </option>
            ))}
          </SelectField>
        </div>

        {sectionId !== '' && (
          <p className="mt-4 text-sm text-muted">
            Class teacher:{' '}
            {activeClassTeacher === undefined ? (
              <span className="text-subtle">none assigned</span>
            ) : (
              <span className="text-ink">{activeClassTeacher.teacherName}</span>
            )}
          </p>
        )}
      </Card>

      {sectionId === '' ? (
        <Card className="p-5">
          <p className="py-6 text-center text-sm text-subtle">
            Choose a class to see who teaches it.
          </p>
        </Card>
      ) : assignments.isPending ? (
        <LoadingState label="Loading assignments" />
      ) : assignments.error !== null ? (
        <ErrorState error={assignments.error} onRetry={() => void assignments.refetch()} />
      ) : assignments.data === undefined || assignments.data.length === 0 ? (
        <Card className="p-5">
          <p className="py-6 text-center text-sm text-subtle">
            No teachers assigned to this class yet.
          </p>
        </Card>
      ) : (
        <Card className="p-5">
          <ul className="divide-y divide-line">
            {assignments.data.map((assignment) => (
              <li key={assignment.id} className="flex flex-wrap items-start justify-between gap-3 py-3">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-ink">
                    {assignment.subjectName}
                    <span className="ml-2 font-normal text-muted">{assignment.teacherName}</span>
                  </p>
                  <p className="mt-0.5 text-xs text-subtle">
                    {humanise(assignment.assignmentType)} · from{' '}
                    {formatDate(assignment.effectiveFrom)}
                    {assignment.effectiveTo !== undefined &&
                      ` until ${formatDate(assignment.effectiveTo)}`}
                  </p>
                </div>

                <div className="flex shrink-0 items-center gap-2">
                  {assignment.status === 'ACTIVE' ? (
                    <Badge tone="success">Active</Badge>
                  ) : (
                    <Badge tone="neutral">Ended</Badge>
                  )}
                  {can(PERMISSIONS.TEACHER_ASSIGN) && assignment.status === 'ACTIVE' && (
                    <Button size="sm" variant="ghost" onClick={() => setEnding(assignment)}>
                      End
                    </Button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </Card>
      )}

      <Modal
        open={formOpen}
        onClose={() => setFormOpen(false)}
        title="Assign a teacher"
        description="A subject has one primary teacher per class at a time; assistants and substitutes are unlimited."
        footer={
          <>
            <Button variant="ghost" onClick={() => setFormOpen(false)} disabled={assign.isPending}>
              Cancel
            </Button>
            <Button
              onClick={() => assign.mutate()}
              loading={assign.isPending}
              disabled={
                form.teacherId === '' || form.academicSubjectId === '' || form.effectiveFrom === ''
              }
            >
              Assign
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <SelectField
            label="Subject"
            required
            value={form.academicSubjectId}
            onChange={(event) => setForm({ ...form, academicSubjectId: event.target.value })}
            hint={
              curriculum.data?.length === 0
                ? 'This class has no curriculum yet. Add subjects to it first.'
                : 'From this class’s curriculum'
            }
          >
            <option value="">Select a subject…</option>
            {curriculum.data?.map((entry) => (
              <option key={entry.id} value={entry.id}>
                {entry.subjectName}
              </option>
            ))}
          </SelectField>

          <SelectField
            label="Teacher"
            required
            value={form.teacherId}
            onChange={(event) => setForm({ ...form, teacherId: event.target.value })}
          >
            <option value="">Select a teacher…</option>
            {teachers.data?.data
              .filter((teacher) => teacher.availableForAssignment)
              .map((teacher) => (
                <option key={teacher.id} value={teacher.id}>
                  {teacher.fullName ?? teacher.employeeNumber}
                  {teacher.specialization !== undefined && ` · ${teacher.specialization}`}
                </option>
              ))}
          </SelectField>

          <div className="grid gap-4 sm:grid-cols-2">
            <SelectField
              label="Role"
              required
              value={form.assignmentType}
              onChange={(event) =>
                setForm({ ...form, assignmentType: event.target.value as AssignmentType })
              }
            >
              {TYPES.map((type) => (
                <option key={type} value={type}>
                  {humanise(type)}
                </option>
              ))}
            </SelectField>
            <TextField
              label="Effective from"
              type="date"
              required
              value={form.effectiveFrom}
              onChange={(event) => setForm({ ...form, effectiveFrom: event.target.value })}
            />
          </div>
        </div>
      </Modal>

      <Modal
        open={classTeacherOpen}
        onClose={() => setClassTeacherOpen(false)}
        title="Set the class teacher"
        description="Pastoral charge, separate from teaching a subject. Naming a new one steps the current class teacher down the day before."
        footer={
          <>
            <Button variant="ghost" onClick={() => setClassTeacherOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => assignClassTeacher.mutate()}
              loading={assignClassTeacher.isPending}
              disabled={classTeacherForm.teacherId === '' || classTeacherForm.effectiveFrom === ''}
            >
              Assign
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <SelectField
            label="Teacher"
            required
            value={classTeacherForm.teacherId}
            onChange={(event) =>
              setClassTeacherForm({ ...classTeacherForm, teacherId: event.target.value })
            }
          >
            <option value="">Select a teacher…</option>
            {teachers.data?.data
              .filter((teacher) => teacher.availableForAssignment)
              .map((teacher) => (
                <option key={teacher.id} value={teacher.id}>
                  {teacher.fullName ?? teacher.employeeNumber}
                </option>
              ))}
          </SelectField>

          <div className="grid gap-4 sm:grid-cols-2">
            <SelectField
              label="Role"
              required
              value={classTeacherForm.role}
              onChange={(event) =>
                setClassTeacherForm({
                  ...classTeacherForm,
                  role: event.target.value as ClassTeacherRole,
                })
              }
            >
              <option value="CLASS_TEACHER">Class teacher</option>
              <option value="ASSISTANT_CLASS_TEACHER">Assistant class teacher</option>
            </SelectField>
            <TextField
              label="Effective from"
              type="date"
              required
              value={classTeacherForm.effectiveFrom}
              onChange={(event) =>
                setClassTeacherForm({ ...classTeacherForm, effectiveFrom: event.target.value })
              }
            />
          </div>
        </div>
      </Modal>

      <Modal
        open={ending !== null}
        onClose={() => setEnding(null)}
        title="End this assignment"
        description="The row is kept and dated out: the timetable entries and coursework set under it remain real."
        footer={
          <>
            <Button variant="ghost" onClick={() => setEnding(null)}>
              Cancel
            </Button>
            <Button onClick={() => end.mutate()} loading={end.isPending} disabled={endDate === ''}>
              End assignment
            </Button>
          </>
        }
      >
        <TextField
          label="Last day"
          type="date"
          required
          value={endDate}
          onChange={(event) => setEndDate(event.target.value)}
        />
      </Modal>
    </div>
  );
}
